/**
 * Pagina /superadmin/lotes — Sprint 13 S13-11.
 *
 * Mantenedor del super_admin para la carga inicial del barrio
 * (LIVE: 71 casas) y altas/edicion posteriores. Cumple convencion
 * doctrinaria de Portal vs Superadmin §4.2.
 *
 * UI: listado con search por identificador + filtro por sector +
 * boton "+ Nueva casa" (modal de creacion) + boton "Editar" por fila
 * (modal de edicion). NO permite cambiar `identificador` post-creacion.
 *
 * RBAC: require_super_admin (cuenta institucional administracion@).
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  actualizarLote,
  crearLote,
  eliminarLote,
  importLotesCsv,
  listLotes,
} from '../../lib/api/lotes';
import type {
  LoteCreate,
  LoteResumen,
  LoteUpdate,
  Sector,
} from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { ActionButton } from '../../components/ui/ActionButton';
import {
  Table,
  TBody,
  THead,
  Th,
  Tr,
  Td,
} from '../../components/ui/DataTable';
import { PageHeader } from '../../components/layout/PageHeader';
import { ImportCsvSection } from '../../components/ImportCsvSection';
import type { CsvColumna } from '../../components/ImportCsvSection';

const SECTORES: Sector[] = ['Oriente', 'Poniente'];

const LOTES_CSV_COLUMNAS: CsvColumna[] = [
  {
    nombre: 'identificador',
    obligatoria: true,
    ejemplo: '6931',
    notas: 'Identificador único de la casa. No editable después.',
  },
  {
    nombre: 'direccion',
    obligatoria: true,
    ejemplo: 'Calle Mar 6931',
  },
  {
    nombre: 'sector',
    obligatoria: true,
    ejemplo: 'Oriente',
    notas: "Solo 'Oriente' o 'Poniente' (con mayúscula).",
  },
  {
    nombre: 'notas',
    obligatoria: false,
    ejemplo: '',
    notas: 'Puede estar vacío.',
  },
];

interface ModalCrearProps {
  onCerrar: () => void;
  onCreado: (lote: LoteResumen) => void;
}

function ModalCrear({ onCerrar, onCreado }: ModalCrearProps) {
  const [identificador, setIdentificador] = useState('');
  const [direccion, setDireccion] = useState('');
  const [sector, setSector] = useState<Sector>('Oriente');
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valido =
    identificador.trim().length > 0 && direccion.trim().length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: LoteCreate = {
        identificador: identificador.trim(),
        direccion: direccion.trim(),
        sector,
        notas: notas.trim() || null,
      };
      const creado = await crearLote(payload);
      onCreado(creado);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Nueva casa"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Nueva casa
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Identificador (# casa)</span>
          <input
            type="text"
            value={identificador}
            onChange={(e) => setIdentificador(e.target.value)}
            placeholder="6931"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
            maxLength={50}
          />
          <span className="text-xs text-text-muted">
            No se podrá editar después.
          </span>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Dirección</span>
          <input
            type="text"
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            placeholder="Calle Mar 6931"
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
            maxLength={255}
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Sector</span>
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value as Sector)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          >
            {SECTORES.map((s) => (
              <option key={s} value={s} className="bg-bg-sunken text-text-primary">
                {s}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Notas (opcional)</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valido || submitting}
          >
            {submitting ? 'Creando…' : 'Crear casa'}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface ModalEditarProps {
  lote: LoteResumen;
  onCerrar: () => void;
  onActualizado: (lote: LoteResumen) => void;
}

function ModalEditar({ lote, onCerrar, onActualizado }: ModalEditarProps) {
  const [direccion, setDireccion] = useState(lote.direccion);
  const [sector, setSector] = useState<Sector>(lote.sector as Sector);
  const [notas, setNotas] = useState(lote.notas ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: LoteUpdate = {
        direccion: direccion.trim() || undefined,
        sector,
        notas: notas.trim() || null,
      };
      const upd = await actualizarLote(lote.id, payload);
      onActualizado(upd);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={`Editar casa ${lote.identificador}`}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Editar casa {lote.identificador}
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Dirección</span>
          <input
            type="text"
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            maxLength={255}
          />
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Sector</span>
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value as Sector)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          >
            {SECTORES.map((s) => (
              <option key={s} value={s} className="bg-bg-sunken text-text-primary">
                {s}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Notas (opcional)</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            placeholder="Vacío = sin notas."
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function LotesPage() {
  const [lotes, setLotes] = useState<LoteResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filtroSector, setFiltroSector] = useState<Sector | 'todos'>('todos');
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [editando, setEditando] = useState<LoteResumen | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [eliminandoId, setEliminandoId] = useState<string | null>(null);
  const [errorEliminar, setErrorEliminar] = useState<string | null>(null);

  async function handleEliminar(lote: LoteResumen) {
    if (eliminandoId) return;
    const ok = window.confirm(
      `¿Eliminar la casa ${lote.identificador}? Solo si no tiene relaciones (tenencias, cuotas, pagos, recursos, vehículos). Acción NO reversible.`,
    );
    if (!ok) return;
    setEliminandoId(lote.id);
    setErrorEliminar(null);
    try {
      await eliminarLote(lote.id);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setErrorEliminar(`Casa ${lote.identificador}: ${detail}`);
      } else {
        setErrorEliminar(
          `Casa ${lote.identificador}: error de red al eliminar`,
        );
      }
    } finally {
      setEliminandoId(null);
    }
  }

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listLotes()
      .then((data) => {
        if (!cancelado) setLotes(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar lotes`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  const lotesFiltrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lotes.filter((l) => {
      if (filtroSector !== 'todos' && l.sector !== filtroSector) return false;
      if (q && !l.identificador.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [lotes, search, filtroSector]);

  const conteo = useMemo(() => {
    return {
      total: lotes.length,
      oriente: lotes.filter((l) => l.sector === 'Oriente').length,
      poniente: lotes.filter((l) => l.sector === 'Poniente').length,
    };
  }, [lotes]);

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Mantenedor — Carga inicial"
        title="Casas del barrio"
        description="Carga inicial de las 71 casas del barrio (LIVE). El identificador (# casa) no se puede editar tras crear la casa; el resto sí."
        action={
          <Button variant="primary" onClick={() => setMostrarCrear(true)}>
            + Nueva casa
          </Button>
        }
      />

      <ImportCsvSection
        titulo="Importar casas desde CSV"
        descripcion="Carga masiva de casas (útil para las 71 casas del LIVE inicial). Cada fila se procesa por separado: las exitosas se crean, los errores se reportan abajo sin detener el batch. Los identificadores duplicados se reportan como error y la fila no se crea."
        columnas={LOTES_CSV_COLUMNAS}
        nombrePlantilla="plantilla-casas-calbucura.csv"
        subir={importLotesCsv}
        onResultado={() => setRefreshKey((k) => k + 1)}
      />

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar # casa…"
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
        />
        <select
          value={filtroSector}
          onChange={(e) =>
            setFiltroSector(e.target.value as Sector | 'todos')
          }
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          aria-label="Filtrar por sector"
        >
          <option value="todos">Todos los sectores</option>
          <option value="Oriente">Oriente</option>
          <option value="Poniente">Poniente</option>
        </select>
        <span className="text-xs text-text-muted">
          Total {conteo.total} · Oriente {conteo.oriente} · Poniente{' '}
          {conteo.poniente}
        </span>
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}
      {errorEliminar && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {errorEliminar}{' '}
          <button
            type="button"
            onClick={() => setErrorEliminar(null)}
            className="underline ml-2"
          >
            Cerrar
          </button>
        </div>
      )}
      {!loading && !error && lotesFiltrados.length === 0 && (
        <p className="text-sm text-text-muted">
          {lotes.length === 0
            ? 'No hay casas registradas. Usa "+ Nueva casa" para cargar la primera.'
            : 'No hay casas que coincidan con el filtro.'}
        </p>
      )}
      {!loading && !error && lotesFiltrados.length > 0 && (
        <Table>
          <THead>
            <Th># casa</Th>
            <Th>Dirección</Th>
            <Th>Sector</Th>
            <Th>Notas</Th>
            <Th align="right">Acciones</Th>
          </THead>
          <TBody>
            {lotesFiltrados.map((lote) => (
              <Tr key={lote.id} data-testid="fila-lote">
                <Td tone="primary" className="font-medium">
                  {lote.identificador}
                </Td>
                <Td>{lote.direccion}</Td>
                <Td>{lote.sector}</Td>
                <Td tone="muted">{lote.notas || '—'}</Td>
                <Td align="right" className="whitespace-nowrap">
                  <div className="inline-flex gap-2">
                    <ActionButton
                      variant="edit"
                      onClick={() => setEditando(lote)}
                    >
                      Editar
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      onClick={() => handleEliminar(lote)}
                      disabled={eliminandoId === lote.id}
                    >
                      {eliminandoId === lote.id ? 'Eliminando…' : 'Eliminar'}
                    </ActionButton>
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {mostrarCrear && (
        <ModalCrear
          onCerrar={() => setMostrarCrear(false)}
          onCreado={() => {
            setMostrarCrear(false);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {editando && (
        <ModalEditar
          lote={editando}
          onCerrar={() => setEditando(null)}
          onActualizado={() => {
            setEditando(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
